import { type CSSProperties, useLayoutEffect, useRef } from 'react';

import { cn } from '@shared/lib/cn';
import { formatNumber } from '@shared/lib/format';

import { CHART_SIZE, GRADE_BG_CLASS, HEATMAP_LAYOUT } from './chart.constant';
import { CHART_TEXT } from './chart.text';
import { ChartLegendDot } from './chart-legend-dot.ui';
import { useChartTooltip } from './chart-tooltip.hook';
import { ChartTooltip } from './chart-tooltip.ui';
import { heatmapGradeOf } from './heatmap.lib';
import type { HeatmapRange, HeatmapRow } from './model/heatmap.types';

export interface HeatmapProps {
  rows: HeatmapRow[];
  /** 기간 이름. 받은 순서대로 왼쪽부터 그린다(정렬은 부르는 쪽이 한다) */
  columns: string[];
  ranges: HeatmapRange[];
  formatValue?: (value: number, column: string) => string;
  /** 줄 높이. 기본 32 */
  rowHeight?: number;
  /**
   * 칸이 많아 가로로 스크롤할 때 처음 보일 쪽. 기본 start(왼쪽).
   * 열 순서(과거 → 최신 등)는 부르는 쪽이 정하므로 최신 쪽을 먼저 보이려면 그쪽을 넘긴다(DD-54 #5)
   */
  initialScroll?: 'start' | 'end';
  className?: string;
}

/**
 * 히트맵(줄 = 기업, 칸 = 기간, 색 = 등급). 직접 그린 격자(DD-54 #1).
 * 맨 위 가운데 범례 줄(ChartLegendDot, kind=grade) + 기간 이름 줄 24 + 줄 높이 × 줄 수.
 * 칸 = `dataviz/grade/*`, 칸 사이 안쪽 1px `dataviz/gap`(맞닿으면 2px). 줄 이름은 왼쪽에 고정(너비 100, 오른쪽 정렬)하고
 * 칸이 좁아지면(40 미만) 칸 영역만 가로로 스크롤한다. 칸에 올리면 ChartTooltip: 제목 = 기업, 줄 = "값 (기간)".
 */
export function Heatmap({
  rows,
  columns,
  ranges,
  formatValue,
  rowHeight = CHART_SIZE.heatmapRowHeight,
  initialScroll = 'start',
  className,
}: HeatmapProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { tooltip, show, hide, hideNow, bubbleProps } = useChartTooltip();
  const graphHeight = CHART_SIZE.heatmapHeaderHeight + rowHeight * rows.length;

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (element && initialScroll === 'end') element.scrollLeft = element.scrollWidth;
  }, [initialScroll, columns.length]);

  const gridStyle: CSSProperties = {
    gridTemplateColumns: `${HEATMAP_LAYOUT.labelWidth}px repeat(${columns.length}, minmax(${HEATMAP_LAYOUT.minCellWidth}px, 1fr))`,
    gridTemplateRows: `${CHART_SIZE.heatmapHeaderHeight}px repeat(${rows.length}, ${rowHeight}px)`,
  };

  const valueText = (value: number | null, column: string) =>
    value === null
      ? '-'
      : (formatValue?.(value, column) ?? formatNumber(value, { maximumFractionDigits: 2 }));

  return (
    <div className={cn('flex w-full flex-col gap-2 bg-bg-surface', className)}>
      <ul className="flex h-4 items-center justify-center gap-3">
        {ranges.map((range) => (
          <li key={`${range.grade}`} className="flex">
            <ChartLegendDot kind="grade" step={range.grade}>
              {range.label}
            </ChartLegendDot>
          </li>
        ))}
      </ul>
      <div
        ref={scrollRef}
        role="img"
        aria-label={CHART_TEXT.summary.heatmap(
          rows.length,
          columns.length,
          columns[0],
          columns[columns.length - 1],
        )}
        data-chart="heatmap"
        style={{ height: graphHeight }}
        onScroll={hideNow}
        className="scrollbar-none w-full overflow-x-auto overflow-y-hidden"
      >
        <div style={gridStyle} className="grid min-w-full">
          {/* 기간 이름 줄 — 왼쪽 위 빈 칸도 고정 */}
          <span className="sticky left-0 z-1 bg-bg-surface" />
          {columns.map((column, columnIndex) => (
            <span
              key={`${columnIndex}-${column}`}
              data-slot="column"
              className="flex items-center justify-center truncate text-dataviz-axis typo-caption"
            >
              {column}
            </span>
          ))}
          {rows.map((row) => [
            <span
              key={`${row.id}-label`}
              data-slot="row-label"
              title={row.label}
              className="sticky left-0 z-1 flex items-center justify-end truncate bg-bg-surface pr-2 text-dataviz-axis typo-caption"
            >
              <span className="truncate">{row.label}</span>
            </span>,
            ...columns.map((column, columnIndex) => {
              const value = row.values[columnIndex] ?? null;
              const grade = heatmapGradeOf(value, ranges);
              const gradeClass = GRADE_BG_CLASS[`${grade}`];
              const key = `${row.id}-${columnIndex}`;
              return (
                <span
                  key={key}
                  data-slot="cell"
                  data-grade={grade}
                  data-active={tooltip?.key === key || undefined}
                  onPointerEnter={(event) => {
                    const rect = event.currentTarget.getBoundingClientRect();
                    show({
                      key,
                      content: {
                        title: row.label,
                        rows: [
                          {
                            color: { kind: 'grade', step: grade },
                            name: `${valueText(value, column)} (${column})`,
                            value: '',
                          },
                        ],
                      },
                      anchor: { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
                    });
                  }}
                  onPointerLeave={hide}
                  className={cn('border border-dataviz-gap data-active:brightness-95', gradeClass)}
                />
              );
            }),
          ])}
        </div>
      </div>
      {tooltip ? (
        <ChartTooltip
          {...tooltip.content}
          anchor={tooltip.anchor}
          placement={tooltip.placement}
          {...bubbleProps}
        />
      ) : null}
    </div>
  );
}
